<template>
  <base-modal
    has-close-btn
    image-gallery
    :show="show"
    class="tw-dark"
    :full-width="!!activeFile?.iframe"
    @close:modal="reset"
  >
    <div
      class="tw-relative tw-flex tw-flex-col tw-justify-center tw-items-center tw-mx-auto"
      :class="{
        'tw-w-96 tw-h-64': !firstLoad,
        'tw-hidden': activeFile?.type !== 'image',
      }"
      :style="{
        height: `calc(min(${containerHeight}px, ${windowHeight}px - ${MODAL_MARGIN}px))`,
      }"
    >
      <img
        ref="imgTarget"
        class="tw-object-contain"
        :class="
          files.length > 1
            ? 'tw-h-[calc(100%-8rem)] -tw-mt-[8rem]'
            : 'tw-h-full'
        "
        :src="activeFile.image"
        :alt="activeFile.name"
      />
      <div
        class="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-black-opacity tw-overflow-hidden"
        :class="{ 'tw-hidden': loaded }"
      >
        <base-loading-icon show />
      </div>
    </div>

    <div
      v-if="activeFile?.iframe"
      class="tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-max-w-full tw-px-6"
    >
      <view-iframe :link="activeFile.link" class="tw-mb-32" />
    </div>

    <slot name="slider" />

    <template #controls>
      <div
        v-if="files.length > 1"
        class="tw-absolute tw-pb-4 tw-w-full tw-text-center tw-space-x-2 tw-pointer-events-none tw-transition-all tw-bottom-20 tw-duration-200"
      >
        <div
          class="tw-px-2 game-tile-background-color game-button-radius tw-w-fit tw-mx-auto"
        >
          <span>{{ activeItemId + 1 }} {{ $t('of') }} {{ files.length }}</span>
        </div>
      </div>

      <div
        v-if="files.length > 1"
        class="tw-absolute tw--left-4 sm:tw-left-0 tw-h-full tw-w-10 tw-flex tw-items-center tw-pointer-events-none -tw-mt-[8rem]"
      >
        <base-button
          basic-btn
          text-link-hover
          primary
          icon="chevron-left"
          class="tw-size-10 tw-text-h3 md:tw-text-5xl tw-pointer-events-auto"
          @click="changeImage(-1)"
        />
      </div>

      <div
        v-if="files.length > 1"
        class="tw-absolute tw--right-4 sm:tw-right-0 tw-h-full tw-w-10 tw-flex tw-items-center tw-pointer-events-none -tw-mt-[8rem]"
      >
        <base-button
          basic-btn
          text-link-hover
          primary
          icon="chevron-right"
          class="tw-size-10 tw-text-h3 md:tw-text-5xl tw-pointer-events-auto"
          @click="changeImage(1)"
        />
      </div>
    </template>
  </base-modal>
</template>

<script>
import { containerHeight, windowHeight } from '@composables/useWindowEvents.js'
import { onUnmounted, onMounted, computed, toRefs, watch, ref } from 'vue'
import ViewIframe from '@shared/components/View/ViewIframe.vue'
import { MODAL_MARGIN } from '@config/options.js'
import { isObjEmpty } from '@helpers/utils.js'

export default {
  components: {
    ViewIframe,
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    imageId: {
      type: Number,
      default: 0,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close:modal', 'change:image', 'input:keypress'],
  setup(props, { emit }) {
    const { imageId, files, show } = toRefs(props)
    const activeItemId = ref(null)
    const imgTarget = ref(null)
    const loaded = ref(false)
    const firstLoad = ref(false)

    watch(
      imageId,
      (id) => {
        activeItemId.value = id
      },
      { immediate: true }
    )

    const activeFile = computed(() =>
      !isObjEmpty(files.value) ? files.value[activeItemId.value] : null
    )

    function onImageLoad() {
      loaded.value = true
      firstLoad.value = true
    }

    watch(
      show,
      (_show) => {
        _show
          ? imgTarget.value?.addEventListener('load', onImageLoad)
          : imgTarget.value?.removeEventListener('load', onImageLoad)
      },
      { flush: 'post' }
    )

    watch(
      activeFile,
      () => {
        loaded.value = !!imgTarget.value?.complete
      },
      { flush: 'post' }
    )

    function reset() {
      activeItemId.value = imageId.value
      emit('close:modal')
    }

    function changeImage(dir) {
      emit('change:image', dir)
      activeItemId.value =
        (activeItemId.value + dir + files.value.length) % files.value.length
    }

    function _handleKeyPress(e) {
      if (show.value) {
        switch (e.key) {
          case 'Escape':
            reset()
            break
          case 'Tab':
            e.preventDefault()
            changeImage(e.shiftKey ? -1 : 1)
            break
          case 'ArrowLeft':
            e.preventDefault()
            changeImage(-1)
            break
          case 'ArrowRight':
            e.preventDefault()
            changeImage(1)
            break
        }
        emit('input:keypress', e)
      }
    }

    onMounted(() => window.addEventListener('keydown', _handleKeyPress))
    onUnmounted(() => {
      imgTarget.value?.removeEventListener('load', onImageLoad)
      window.removeEventListener('keydown', _handleKeyPress)
    })

    return {
      containerHeight,
      MODAL_MARGIN,
      windowHeight,
      activeItemId,
      changeImage,
      activeFile,
      imgTarget,
      firstLoad,
      loaded,
      reset,
    }
  },
}
</script>
